import { NotificationType } from '../enums';
import { emailModeForType } from '../notifications/notification-prefs';
import type {
  NotificationEmailMode,
  NotificationPrefsV2,
} from '../notifications/notification-channels';
import { CommunityDigestSchedule } from '../enums';

export type CommunityNotificationEventKey =
  | 'replies'
  | 'mentions'
  | 'pins'
  | 'mod_actions'
  | 'welcome'
  | 'billing'
  | 'application_reviewed';

export type CommunityNotificationEmailMode = 'inherit' | 'realtime' | 'digest';

export type CommunityMembershipNotificationPrefs = {
  version: 1;
  events: Record<CommunityNotificationEventKey, boolean>;
  emailMode: CommunityNotificationEmailMode;
  pushEnabled: boolean;
};

export type CommunityNotificationPrefsView = {
  muted: boolean;
  emailMode: CommunityNotificationEmailMode;
  pushEnabled: boolean;
  digestSchedule: CommunityDigestSchedule | string;
  events: {
    replies: boolean;
    mentions: boolean;
    pins: boolean;
    modActions: boolean;
    welcome: boolean;
    billing: boolean;
    applicationReviewed: boolean;
  };
};

const EVENT_KEYS: CommunityNotificationEventKey[] = [
  'replies',
  'mentions',
  'pins',
  'mod_actions',
  'welcome',
  'billing',
  'application_reviewed',
];

export const DEFAULT_COMMUNITY_MEMBERSHIP_NOTIFICATION_PREFS: CommunityMembershipNotificationPrefs =
  {
    version: 1,
    events: {
      replies: true,
      mentions: true,
      pins: true,
      mod_actions: true,
      welcome: true,
      billing: true,
      application_reviewed: true,
    },
    emailMode: 'inherit',
    pushEnabled: true,
  };

const COMMUNITY_SCOPED_TYPES = new Set<NotificationType>([
  NotificationType.COMMUNITY_JOIN_WELCOME,
  NotificationType.COMMUNITY_REPLY,
  NotificationType.COMMUNITY_MENTION,
  NotificationType.COMMUNITY_PIN,
  NotificationType.COMMUNITY_MOD_ACTION,
  NotificationType.COMMUNITY_BILLING,
  NotificationType.COMMUNITY_APPLICATION_REVIEWED,
]);

export function isCommunityScopedNotificationType(type: NotificationType): boolean {
  return COMMUNITY_SCOPED_TYPES.has(type);
}

export function communityNotificationTypeToEventKey(
  type: NotificationType,
): CommunityNotificationEventKey | null {
  switch (type) {
    case NotificationType.COMMUNITY_REPLY:
      return 'replies';
    case NotificationType.COMMUNITY_MENTION:
      return 'mentions';
    case NotificationType.COMMUNITY_PIN:
      return 'pins';
    case NotificationType.COMMUNITY_MOD_ACTION:
      return 'mod_actions';
    case NotificationType.COMMUNITY_JOIN_WELCOME:
      return 'welcome';
    case NotificationType.COMMUNITY_BILLING:
      return 'billing';
    case NotificationType.COMMUNITY_APPLICATION_REVIEWED:
      return 'application_reviewed';
    default:
      return null;
  }
}

export function parseCommunityMembershipNotificationPrefs(
  raw: unknown,
): CommunityMembershipNotificationPrefs {
  if (!raw || typeof raw !== 'object') {
    return {
      ...DEFAULT_COMMUNITY_MEMBERSHIP_NOTIFICATION_PREFS,
      events: { ...DEFAULT_COMMUNITY_MEMBERSHIP_NOTIFICATION_PREFS.events },
    };
  }

  const input = raw as Record<string, unknown>;
  const eventsRaw = (input.events ?? {}) as Partial<Record<CommunityNotificationEventKey, unknown>>;
  const events = { ...DEFAULT_COMMUNITY_MEMBERSHIP_NOTIFICATION_PREFS.events };

  for (const key of EVENT_KEYS) {
    if (typeof eventsRaw[key] === 'boolean') {
      events[key] = eventsRaw[key] as boolean;
    }
  }

  const emailMode =
    input.emailMode === 'realtime' || input.emailMode === 'digest'
      ? input.emailMode
      : 'inherit';

  const pushEnabled =
    typeof input.pushEnabled === 'boolean'
      ? input.pushEnabled
      : DEFAULT_COMMUNITY_MEMBERSHIP_NOTIFICATION_PREFS.pushEnabled;

  return {
    version: 1,
    events,
    emailMode,
    pushEnabled,
  };
}

export function normalizeCommunityMembershipNotificationPrefs(
  partial: Partial<{
    replies: boolean;
    mentions: boolean;
    pins: boolean;
    modActions: boolean;
    welcome: boolean;
    billing: boolean;
    applicationReviewed: boolean;
    emailMode: CommunityNotificationEmailMode;
    pushEnabled: boolean;
  }>,
  current: CommunityMembershipNotificationPrefs = DEFAULT_COMMUNITY_MEMBERSHIP_NOTIFICATION_PREFS,
): CommunityMembershipNotificationPrefs {
  const next = parseCommunityMembershipNotificationPrefs(current);

  if (typeof partial.replies === 'boolean') next.events.replies = partial.replies;
  if (typeof partial.mentions === 'boolean') next.events.mentions = partial.mentions;
  if (typeof partial.pins === 'boolean') next.events.pins = partial.pins;
  if (typeof partial.modActions === 'boolean') next.events.mod_actions = partial.modActions;
  if (typeof partial.welcome === 'boolean') next.events.welcome = partial.welcome;
  if (typeof partial.billing === 'boolean') next.events.billing = partial.billing;
  if (typeof partial.applicationReviewed === 'boolean') {
    next.events.application_reviewed = partial.applicationReviewed;
  }
  if (
    partial.emailMode === 'inherit' ||
    partial.emailMode === 'realtime' ||
    partial.emailMode === 'digest'
  ) {
    next.emailMode = partial.emailMode;
  }
  if (typeof partial.pushEnabled === 'boolean') {
    next.pushEnabled = partial.pushEnabled;
  }

  return next;
}

export function mapCommunityNotificationPrefsView(params: {
  prefs: CommunityMembershipNotificationPrefs;
  muted: boolean;
  digestSchedule?: CommunityDigestSchedule | string | null;
}): CommunityNotificationPrefsView {
  return {
    muted: params.muted,
    emailMode: params.prefs.emailMode,
    pushEnabled: params.prefs.pushEnabled,
    digestSchedule: params.digestSchedule ?? CommunityDigestSchedule.INHERIT,
    events: {
      replies: params.prefs.events.replies,
      mentions: params.prefs.events.mentions,
      pins: params.prefs.events.pins,
      modActions: params.prefs.events.mod_actions,
      welcome: params.prefs.events.welcome,
      billing: params.prefs.events.billing,
      applicationReviewed: params.prefs.events.application_reviewed,
    },
  };
}

export function isCommunityNotificationEventEnabled(
  prefs: CommunityMembershipNotificationPrefs,
  event: CommunityNotificationEventKey,
): boolean {
  return prefs.events[event] !== false;
}

export function shouldDeliverCommunityNotification(params: {
  type: NotificationType;
  prefs: CommunityMembershipNotificationPrefs;
  masterMuted: boolean;
}): boolean {
  if (params.masterMuted) return false;
  const event = communityNotificationTypeToEventKey(params.type);
  if (!event) return true;
  return isCommunityNotificationEventEnabled(params.prefs, event);
}

export function shouldDeliverCommunityPushNotification(params: {
  type: NotificationType;
  prefs: CommunityMembershipNotificationPrefs;
  masterMuted: boolean;
}): boolean {
  if (!params.prefs.pushEnabled) return false;
  return shouldDeliverCommunityNotification(params);
}

export function resolveCommunityDigestSchedule(
  communitySchedule: CommunityDigestSchedule | string | null | undefined,
): 'daily' | 'weekly' | 'off' {
  switch (communitySchedule) {
    case CommunityDigestSchedule.DAILY:
      return 'daily';
    case CommunityDigestSchedule.WEEKLY:
      return 'weekly';
    case CommunityDigestSchedule.OFF:
      return 'off';
    default:
      return 'daily';
  }
}

export function resolveCommunityNotificationEmailMode(
  globalPrefs: NotificationPrefsV2,
  membershipPrefs: CommunityMembershipNotificationPrefs,
  type: NotificationType,
  communityDigestSchedule?: CommunityDigestSchedule | string | null,
): NotificationEmailMode {
  if (!isCommunityScopedNotificationType(type)) {
    return emailModeForType(globalPrefs, type);
  }

  if (membershipPrefs.emailMode === 'inherit') {
    const schedule = resolveCommunityDigestSchedule(communityDigestSchedule);
    if (schedule === 'off') return 'off';
    if (schedule === 'weekly') return 'weekly_digest';
    if (schedule === 'daily') return 'daily_digest';
    return emailModeForType(globalPrefs, type);
  }

  if (membershipPrefs.emailMode === 'realtime') {
    return 'missed_only';
  }

  return globalPrefs.digest.frequency === 'weekly' ? 'weekly_digest' : 'daily_digest';
}
